import React, { useState, useEffect, useRef } from 'react';
import {
    Stack,
    Paper,
    Text,
    Group,
    Badge,
    ActionIcon,
    Button,
    Modal,
    LoadingOverlay,
    Table,
    Tooltip,
} from '@mantine/core';
import {
    useRules,
    Rule,
    RuleType,
} from '../../hooks/useRules';
import { showSuccessNotification, showErrorNotification } from '../../utils/notificationUtils';

import pauseIcon from '../../assets/icons/pause.svg';
import playIcon from '../../assets/icons/play.svg';
import editIcon from '../../assets/icons/edit.svg';
import trashIcon from '../../assets/icons/trash.svg';
import settingsIcon from '../../assets/icons/settings.svg';

interface RulesListProps {
    groupId?: string | number;
    accessToken: string;
    onSelect: (rule: Rule) => void;
    ruleType?: RuleType;
    refreshCounter?: number;
    accounts?: { id: string; name: string; institution_name?: string }[];
}

export const RulesList: React.FC<RulesListProps> = ({
    groupId: propGroupId,
    accessToken,
    onSelect,
    ruleType,
    refreshCounter,
    accounts = [],
}) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null);
    const groupId = propGroupId ? Number(propGroupId) : undefined;

    // Determine the effective rule type. If ruleType prop is provided, use it.
    // Otherwise, if groupId is provided, infer 'group_expense'.
    // If neither is provided, leave it undefined to fetch all rule types.
    const effectiveRuleType: RuleType | undefined =
        ruleType ? ruleType :
            (groupId ? 'group_expense' : undefined);

    const {
        rules,
        loading,
        error,
        deleteRule,
        toggleRuleStatus,
        refreshRules,
    } = useRules(accessToken, effectiveRuleType, groupId);

    // Effect to refresh rules when refreshCounter changes
    useEffect(() => {
        if (typeof refreshCounter === 'number') {
            refreshRules();
        }
    }, [refreshCounter, refreshRules]);

    const handleToggleStatus = async (id: number, enabled: boolean) => {
        try {
            await toggleRuleStatus(id, !enabled);
            showSuccessNotification(`Rule ${enabled ? 'disabled' : 'enabled'} successfully`);
            refreshRules();
        } catch (err) {
            showErrorNotification(err, 'Failed to update rule status');
        }
    };

    const handleDeleteRule = async () => {
        if (!selectedRuleId) return;

        try {
            await deleteRule(selectedRuleId);
            showSuccessNotification('Rule deleted successfully');
            refreshRules();
        } catch (err) {
            showErrorNotification(err, 'Failed to delete rule');
        } finally {
            setShowDeleteModal(false);
            setSelectedRuleId(null);
        }
    };

    // Helper function to format rule conditions for display
    const formatRuleCondition = (rule: Rule) => {
        const formatField = (field: string) => {
            if (field === 'description') return 'Description';
            if (field === 'location') return 'Location';
            if (field === 'account') return 'Account';
            return field.replace(/_/g, ' ');
        };
        const formatOperator = (op: string) => op.replace(/_/g, ' ');

        // If no conditions, return empty string
        if (!rule.conditions || rule.conditions.length === 0) {
            return '';
        }

        // Format each condition
        const formattedConditions = rule.conditions.map(condition => {
            let valueDisplay = condition.value;

            if (condition.field === 'amount' && condition.operator === 'between') {
                const [min, max] = condition.value.split(',');
                valueDisplay = `${min} to ${max}`;
            } else if (condition.field === 'recurring') {
                valueDisplay = condition.value === 'true' ? 'Yes' : 'No';
            } else if (condition.field === 'account') {
                // Look up the account name from the accounts array
                const account = accounts.find(a => a.id === condition.value);
                valueDisplay = account ? account.name : condition.value;
            }

            return `${formatField(condition.field)} ${formatOperator(condition.operator)} "${valueDisplay}"`;
        });

        // Join conditions with AND
        if (formattedConditions.length === 1) {
            return `When ${formattedConditions[0]}`;
        } else {
            return `When ${formattedConditions.join(' AND ')}`;
        }
    };

    if (error) {
        return (
            <Paper p="xl" withBorder>
                <Text ta="center" c="red">
                    {error}
                </Text>
            </Paper>
        );
    }

    return (
        <Stack>
            <div style={{ position: 'relative' }}>
                <LoadingOverlay visible={loading} />

                {rules.length === 0 ? (
                    <Paper p="xl" withBorder>
                        <Text ta="center" c="dimmed">
                            No rules found. Create a rule to automatically categorize transactions.
                        </Text>
                    </Paper>
                ) : (
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Rule Name</Table.Th>
                                <Table.Th>Condition</Table.Th>
                                <Table.Th>Status</Table.Th>
                                <Table.Th>Actions</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {rules.map(rule => (
                                <Table.Tr key={rule.id}>
                                    <Table.Td>
                                        <Text fw={500}>{rule.name}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Text size="sm" c="dimmed">
                                            {formatRuleCondition(rule)}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge color={rule.enabled ? 'green' : 'gray'}>
                                            {rule.enabled ? 'Enabled' : 'Disabled'}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                        <Group gap={4}>
                                            <Tooltip label="Edit">
                                                <ActionIcon onClick={() => onSelect(rule)}>
                                                    <img src={editIcon} alt="Edit" width={16} />
                                                </ActionIcon>
                                            </Tooltip>
                                            <Tooltip label={rule.enabled ? 'Disable' : 'Enable'}>
                                                <ActionIcon
                                                    variant="subtle"
                                                    onClick={() => handleToggleStatus(rule.id, rule.enabled)}>
                                                    <img
                                                        src={rule.enabled ? pauseIcon : playIcon}
                                                        alt={rule.enabled ? 'Disable' : 'Enable'}
                                                        width={16}
                                                    />
                                                </ActionIcon>
                                            </Tooltip>
                                            <Tooltip label="Delete">
                                                <ActionIcon
                                                    variant="subtle"
                                                    color="red"
                                                    onClick={() => {
                                                        setSelectedRuleId(rule.id);
                                                        setShowDeleteModal(true);
                                                    }}
                                                >
                                                    <img src={trashIcon} alt="Delete" width={16} />
                                                </ActionIcon>
                                            </Tooltip>
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <Modal
                opened={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                title="Delete Rule"
                centered
            >
                <Stack>
                    <Text>
                        Are you sure you want to delete this rule? This action cannot be undone.
                    </Text>
                    <Group justify="right">
                        <Button variant="subtle" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button color="red" onClick={handleDeleteRule}>
                            Delete
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
}; 